import { useState } from 'react';
import { ChevronDown, Sparkles } from 'lucide-react';
import { cn } from '@/shared/cn';
// fork(oem): the rail greets the persona in the "VIEWING AS" picker, not the
// foundation's seed user — the demo's assistant opened with the morning brief
// for whoever was looking. `SEED_USER` stays the account-menu identity.
import { useOemPersona } from '@/versions/v1/shared/OemPersonaContext';
// fork(oem v1.8): the 06:05 morning briefing sits under the greeting.
import { useChat } from '@/versions/v1/shared/ChatContext';
import { AssistantMessage } from './ChatMessages';
import { ChatComposer } from './ChatComposer';
import { StarterCategories } from './StarterCategories';

function WelcomeGlyph() {
  return (
    <span className="inline-flex flex-shrink-0 [animation:welcome-spark-in_800ms_cubic-bezier(0.22,0.61,0.36,1)_both]">
      <span className="inline-flex [transform-origin:center] hover:[animation:welcome-spark-spin_700ms_cubic-bezier(0.34,1.56,0.64,1)]">
        <Sparkles className="h-[1.4em] w-[1.4em] text-accent-500" strokeWidth={1.6} />
      </span>
    </span>
  );
}

export function WelcomeState({
  onStarterSelect,
}: {
  onStarterSelect: (text: string) => void;
}) {
  // fork(oem): "AI · Service assist" over the OEM+ persona's role and site.
  const { persona } = useOemPersona();
  const { briefing } = useChat();
  const [expanded, setExpanded] = useState(false);
  const greeting = `Morning, ${persona.name}`;

  return (
    // fork(oem v1.8): scrolls — the briefing makes the welcome taller than a
    // short rail. `my-auto` on the column (not `justify-center`) keeps the top
    // reachable when it overflows.
    <div className="scrollbar-subtle @container mx-auto flex min-h-0 w-full max-w-[var(--thread-max-width)] flex-1 flex-col overflow-y-auto px-4 py-8">
      <div className="my-auto flex flex-col items-center gap-6">
        {/* fork(oem): eyebrow + persona greeting + what the assistant covers. */}
        <div className="flex min-w-0 max-w-full flex-col items-center gap-1.5">
          <div
            className="text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            AI · Service assist
          </div>
          <div
            className="flex min-w-0 max-w-full items-center gap-3 text-[clamp(1.25rem,calc(150cqi/var(--greeting-ch)),2rem)] text-zinc-900 dark:text-zinc-100"
            style={{ ['--greeting-ch' as string]: greeting.length }}
          >
            <WelcomeGlyph />
            {/*
              fork(oem): wraps rather than `whitespace-nowrap`. The clamp above
              scales the greeting down by its length, but its 1.25rem floor is
              still too wide for the longer persona names ("Morning, Aftermarket
              Manager", "Morning, Parts & Logistics Coordinator") in a rail at
              1280–1440 px, so the name was being cut off at the rail's edge.
            */}
            <h1 className="min-w-0 text-balance font-normal tracking-tight">{greeting}</h1>
          </div>
          {/* fork(oem v1.8): the "ask me about …" line now closes the briefing. */}
          <p className="max-w-[34rem] text-center text-[13px] text-muted">{persona.desc}</p>
        </div>

        {/*
          fork(oem v1.8): the demo seeded this message on load (demo line
          4099). It is computed from the alert, incident and condition seeds
          (`shared/assistant/briefing.ts`), and becomes the conversation's
          first turn once a question is sent.
        */}
        <section
          aria-label="Morning briefing"
          className="w-full rounded-xl border border-[var(--glass-border)] bg-[var(--background)] px-3 pt-3"
        >
          <div
            className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.1em]"
            style={{ color: 'var(--ink-tertiary)' }}
          >
            <span>Morning briefing</span>
            <span aria-hidden>·</span>
            <span className="tabular-nums">{briefing.time}</span>
          </div>
          {/*
            Collapsed to its opening lines so the composer stays in view in a
            narrow rail; the full text (and its source chip) is one click away.
          */}
          <div
            id="welcome-briefing-body"
            className={cn('relative', !expanded && 'max-h-[8.5rem] overflow-hidden')}
          >
            <AssistantMessage message={briefing} />
            {!expanded ? (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-[var(--background)] to-transparent"
              />
            ) : null}
          </div>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="welcome-briefing-body"
            onClick={() => setExpanded((v) => !v)}
            className="mb-2 mt-1 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-xs font-medium text-[var(--accent)] hover:underline"
          >
            {expanded ? 'Show less' : 'Read the full briefing'}
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', expanded && 'rotate-180')}
              aria-hidden
            />
          </button>
        </section>

        <div className="w-full">
          <ChatComposer welcome />
        </div>

        <StarterCategories onPrefill={onStarterSelect} />
      </div>
    </div>
  );
}
